<template>
    <div>
        <div class="page-content px-4 top">
            <template-breadcrumb v-if="isDataLoaded" :title="details.title" />
            <template-header v-if="isDataLoaded" :title="details.title" />
            <b-card footer-class="p-0" class="rounded-lg">
                <div class="profile-details">
                    <form>
                        <input type="hidden" name="id" :value="templateId" />
                    </form>

                    <loader-profile
                        v-if="!isDataLoaded"
                        :show-image="false"
                    ></loader-profile>

                    <b-row v-else-if="isDataLoaded">
                        <b-col lg="4">
                            <b-form-group
                                label-for="title"
                                label-cols="4"
                                label="Name:"
                                class="title-alignment"
                            >
                                <b-input
                                    v-if="isEditMode"
                                    id="title"
                                    ref="title"
                                    v-model="editDetails.title"
                                    :class="{
                                        'border-danger': errors.has('title')
                                    }"
                                    v-validate="'required'"
                                    name="title"
                                    type="text"
                                    placeholder="Title (required)"
                                    :disabled="isSavingEdit"
                                />
                                <span v-else>{{ details.title }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="type"
                                label-cols="4"
                                label="Type:"
                            >
                                <b-input plaintext :value="templateTypeLabel" />
                            </b-form-group>
                            <b-form-group
                                label-for="created"
                                label-cols="4"
                                label="Created:"
                            >
                                <b-input
                                    plaintext
                                    :value="
                                        _carbonDateDisplay(details.create_at)
                                    "
                                />
                            </b-form-group>
                            <b-form-group
                                label-for="createdBy"
                                label-cols="4"
                                label="Created By:"
                            >
                                <b-input plaintext :value="details.create_by" />
                            </b-form-group>
                        </b-col>
                        <b-col lg="4">
                            <b-form-group
                                label-for="planTerm"
                                label-cols="4"
                                label="Plan Term:"
                                class="d-flex align-items-baseline"
                            >
                                <b-form-select
                                    v-if="isEditMode"
                                    id="plan_terms_value"
                                    v-model="
                                        editDetails.template_attribute.plan_term
                                    "
                                    :options="planTermsOptions"
                                    name="plan_terms_value"
                                    :disabled="isSavingEdit"
                                ></b-form-select>

                                <span v-else>{{
                                    details.plan_terms_value
                                }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="payments"
                                label-cols="4"
                                label="Payments:"
                                class="d-flex align-items-baseline"
                            >
                                <b-form-select
                                    v-if="isEditMode"
                                    id="payment_terms_value"
                                    v-model="
                                        editDetails.template_attribute
                                            .payment_terms
                                    "
                                    :options="paymentTermsOptions"
                                    name="payment_terms_value"
                                    :disabled="isSavingEdit"
                                ></b-form-select>

                                <span v-else>{{
                                    details.payment_terms_value
                                }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="enrollmentFee"
                                label-cols="4"
                                label="Enrollment Fee:"
                                class="d-flex align-items-baseline"
                            >
                                <vet-select
                                    v-if="isEditMode"
                                    module="item"
                                    placeholder="Enrollment Fee (optional)"
                                    name="item"
                                    v-model="editDetails.enrollment_fee"
                                    :selectProps="vetItemProps"
                                    :isAddItemFee="true"
                                    :isWellnessPlan="true"
                                />
                                <template v-else>
                                    <router-link
                                        v-if="details.enrollment_fee"
                                        :to="`/inventory/item/${details.enrollment_fee.encoded_id}`"
                                    >
                                        {{ details.enrollment_fee.name }}
                                    </router-link>
                                </template>
                            </b-form-group>
                            <b-form-group
                                label-for="planFee"
                                label-cols="4"
                                label="Plan Fee:"
                                class="d-flex align-items-baseline"
                            >
                                <vet-select
                                    v-if="isEditMode"
                                    module="item"
                                    placeholder="Fee (required)"
                                    name="item"
                                    v-model="editDetails.plan_fee"
                                    v-validate="'required'"
                                    :selectProps="vetItemProps"
                                    :class="{
                                        'border-danger': errors.has('item')
                                    }"
                                    :showMenu="false"
                                />

                                <template v-else>
                                    <router-link
                                        v-if="details.plan_fee"
                                        :to="`/inventory/item/${details.plan_fee.encoded_id}`"
                                    >
                                        {{ details.plan_fee.name }}
                                    </router-link>
                                </template>
                            </b-form-group>
                        </b-col>
                        <b-col lg="4">
                            <b-form-group
                                label-for="providerCredit"
                                label-cols="4"
                                label="Provider Credit:"
                                class="d-flex align-items-baseline"
                            >
                                <b-form-select
                                    v-if="isEditMode"
                                    id="provider_credit"
                                    v-model="
                                        editDetails.template_attribute
                                            .production_credit
                                    "
                                    name="provider_credit"
                                    :options="productionCreditOptions"
                                    :disabled="isSavingEdit"
                                ></b-form-select>

                                <span v-else>{{
                                    details.provider_credit
                                }}</span>
                            </b-form-group>
                            <b-form-group
                                label-for="enrollmentGoal"
                                label-cols="4"
                                label="Enrollment Goal:"
                                class="d-flex align-items-baseline"
                            >
                                <div v-if="isEditMode" class="d-flex">
                                    <b-input
                                        v-model="
                                            editDetails.template_attribute
                                                .enrollment_goal_value
                                        "
                                        name="enrollment_goal_value"
                                        type="number"
                                        placeholder=""
                                        :disabled="isSavingEdit"
                                        autocomplete="off"
                                        class="w-50"
                                        v-validate="
                                            'numericalWithDecimal|decimal:2'
                                        "
                                        :class="{
                                            'border-danger': errors.has(
                                                'enrollment_goal_value'
                                            )
                                        }"
                                    />
                                    <span
                                        class="px-2 d-flex align-items-center text-nowrap"
                                        >% of</span
                                    >
                                    <b-form-select
                                        id="enrollment_goal"
                                        v-model="
                                            editDetails.template_attribute
                                                .enrollment_goal_id
                                        "
                                        name="enrollment_goal"
                                        :options="enrollmentGoalsOptions"
                                        class="w-100"
                                        :disabled="isSavingEdit"
                                    ></b-form-select>
                                </div>

                                <span v-else>{{
                                    details.enrollment_goal
                                }}</span>
                            </b-form-group>

                            <b-form-group
                                label-for="enrollees"
                                label-cols="4"
                                label="Enrollees:"
                            >
                                <b-input
                                    plaintext
                                    :value="details.wellness_plan_enrollees"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <template v-slot:footer no-body v-if="isDataLoaded">
                    <b-tabs
                        card
                        class="list tab-table-responsive"
                        lazy
                        v-model="activeTabIndex"
                        ref="btabs"
                    >
                        <tab-items
                            :key="1 + keyComponent"
                            ref="tabItems"
                            tabname="tabItems"
                            :tab-count="tabCount.item_count"
                            @reload-item-details="reloadPage"
                            @reload-tab-count="reloadTabCount"
                        />
                        <tab-non-plan-discount
                            :key="2 + keyComponent"
                            ref="tabNonPlanDiscount"
                            tabname="tabNonPlanDiscount"
                            :tab-count="tabCount.non_plan_count"
                            @reload-tab-count="reloadTabCount"
                            @update-item="updateNonPlanDiscount"
                        />
                    </b-tabs>
                </template>
            </b-card>
        </div>

        <ModalItem ref="modalItem" @onsuccess="reloadPage('tabItems', false)" />

        <ModalNonPlan
            ref="modalNonPlan"
            @onsuccess="reloadPage('tabNonPlanDiscount', false)"
        />

        <PopupItemNotDuplicated
            v-if="details.items_not_duplicated"
            :items="details.items_not_duplicated"
        />
    </div>
</template>

<script>
/**
 * @name TemplateWellnessPlan
 * @description Profile of template Wellness plan
 * @see apps/application/views/staff/business/template/wellness/view.php
 * @see apps/application/views/staff/business/template/wellness/navbar.php
 * @see apps/application/controllers/business/template.php
 */

import TemplateHeader from "@/module/settings/template/header.vue";
import TemplateBreadcrumb from "@/module/settings/template/breadcrumb.vue";
import TEMPLATES from "@/constants/templates";
import { DateTime } from "luxon";
import TEMPLATE_SERVICES from "@/services/templates";
import { assignIn, findIndex } from "lodash";
import TabItems from "@/module/settings/template/tabs/wellness-plan/tab-items";
import TabNonPlanDiscount from "@/module/settings/template/tabs/wellness-plan/tab-wellness-plan";
import LoaderProfile from "@/components/elements/loader-profile.vue";
import SettingTemplateMixin from "@/mixins/settings-template";
import ModalItem from "@/components/setting-template/wellness-plan/modal-item";
import ModalNonPlan from "@/components/setting-template/wellness-plan/modal-non-plan";
import PopupItemNotDuplicated from "@/components/setting-template/wellness-plan/popup-item-not-duplicated";
import vetSelect from "@/module/commons/vet-select";
import { map } from "lodash";

export default {
    mixins: [SettingTemplateMixin],
    components: {
        TemplateHeader,
        TemplateBreadcrumb,
        LoaderProfile,
        TabItems,
        TabNonPlanDiscount,
        PopupItemNotDuplicated,
        vetSelect,
        ModalItem,
        ModalNonPlan
    },
    data() {
        return {
            keyComponent: 0,
            activeTabIndex: 0,
            templateId: null,
            isDataLoaded: false,
            isLoadingItems: true,
            popperOpt: {
                placement: "bottom-start",
                modifiers: {
                    flip: {
                        enabled: false
                    }
                },
                positionFixed: true
            },
            tabCount: {
                item_count: 0,
                non_plan_count: 0
            },
            vetItemProps: {
                id: "",
                item_type: "11",
                module: "wellness_element",
                status: "",
                start: "",
                source: ""
            },
            options: {}
        };
    },
    computed: {
        details: {
            get() {
                return this.$store.state.settingsTemplate.details;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setDetails", value);
            }
        },
        editDetails: {
            get() {
                return this.$store.state.settingsTemplate.editDetails;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setEditDetails", value);
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.settingsTemplate.isEditMode;
            }
        },
        isSavingEdit() {
            return this.$store.state.settingsTemplate.isSavingEdit;
        },
        items: {
            get() {
                return this.$store.state.settingsTemplate.items;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setItems", value);
            }
        },
        routeParam() {
            return this.$route.params.id;
        },
        templateTypeLabel() {
            return TEMPLATES.LIST[this.details.type].label;
        },
        planTermsOptions() {
            return this.generateOptions("plan_terms");
        },
        paymentTermsOptions() {
            return this.generateOptions("payment_terms", {
                append: "in advance" // will append options "in advance"
            });
        },
        productionCreditOptions() {
            return this.generateOptions("production_credit");
        },
        enrollmentGoalsOptions() {
            return this.generateOptions("enrollment_goals");
        }
    },
    watch: {
        routeParam() {
            this.fetchData();
        },
        async isEditMode(value) {
            await this.$nextTick();
            if (value && this.$refs.title) {
                this.$refs.title.focus();
            }
        }
    },
    mounted() {
        this.fetchData();

        this.$store.commit(
            "settingsTemplate/setTemplateType",
            TEMPLATES.TYPE.WELLNESS_PLAN
        );
        this.$store.dispatch("references/getUOM");
    },
    beforeDestroy() {
        // clear vuex store
        this.$store.dispatch("settingsTemplate/reset");
    },
    methods: {
        async reloadPageDetails() {
            await this.fetchData();
        },
        errorClass(key) {
            return {
                "border-danger": this.errors.has(key)
            };
        },
        async fetchData() {
            try {
                this.isDataLoaded = false;
                let encodedTemplateId = this.$route.params.id;
                this.templateId = null;

                // fetch details
                let fetchDetails = await this.$http.get(
                    `${process.env.BARRAMUNDI}account/template/${encodedTemplateId}?encoded=true`
                );

                let res = fetchDetails.data.response;
                this.details = res.resources || res.resource;
                this.details.title = this.details.name;
                this.details.create_by = this.details.create_by.label;
                this.details.items_not_duplicated = res.items_not_duplicated;
                this.templateId = this.details.id;
                this.options = res.template_attribute;
                if (this.details && this.details.enrollment_fee) {
                    this.details.enrollment_fee.label = this.generateLabelFee(
                        this.details.enrollment_fee
                    );
                }

                if (this.details && this.details.plan_fee) {
                    this.details.plan_fee.label = this.generateLabelFee(
                        this.details.plan_fee
                    );
                }

                this.isDataLoaded = true;

                this.validateTemplateType(
                    this.details.type,
                    TEMPLATES.TYPE.WELLNESS_PLAN
                );

                this.reloadTabCount();

                // store templateId in vuex
                this.$store.commit("settingsTemplate/setTemplateId", this.templateId); // prettier-ignore
                this.$store.commit(
                    "settingsTemplate/setModalItem",
                    this.$refs.modalItem
                );
            } catch (error) {
                console.log(error);
                this.isDataLoaded = true;
                this._message(error.message, "danger");
            }
        },
        convertToDateFormat(epochTimestamp) {
            if (!epochTimestamp) {
                return "";
            }

            // Convert epoch timestamp in seconds to milliseconds for Luxon
            const dateTime = DateTime.fromMillis(epochTimestamp * 1000);

            // Format the date as desired, e.g., "yyyy-MM-dd"
            return dateTime.toFormat(this._dateFormat);
        },
        async saveDetails() {
            let self = this;

            this.$store.commit("settingsTemplate/toggleSavingEdit", true);

            try {
                let isValid = await this.$validator.validateAll();

                if (!isValid) {
                    this.errors
                        .all()
                        .forEach(error => self._message(error, "danger"));
                    this.$store.commit(
                        "settingsTemplate/toggleSavingEdit",
                        false
                    );
                    return;
                }

                const payload = {
                    plan_name: this.editDetails.title,
                    plan_term: this.editDetails.template_attribute.plan_term,
                    payment_terms: this.editDetails.template_attribute
                        .payment_terms,
                    enrollment_fee_item_id:
                        this.editDetails.enrollment_fee &&
                        this.editDetails.enrollment_fee.id
                            ? this.editDetails.enrollment_fee.id
                            : null,
                    plan_fee_item_id:
                        this.editDetails.plan_fee &&
                        this.editDetails.plan_fee.id
                            ? this.editDetails.plan_fee.id
                            : null,
                    production_credit: this.editDetails.template_attribute
                        .production_credit,
                    enrollment_goal_value: this.editDetails.template_attribute
                        .enrollment_goal_value,
                    enrollment_goal_id: this.editDetails.template_attribute
                        .enrollment_goal_id
                };

                let res = await TEMPLATE_SERVICES.putUpdateGeneric(
                    this.templateId,
                    payload
                );

                this.$store.commit("settingsTemplate/commitEditDetails");

                this.fetchData(res);

                this._message("Record saved");
            } catch (error) {
                console.log(error);
                this._message("Error saving data", "danger");
            }

            this.$store.commit("settingsTemplate/toggleSavingEdit", false);
        },
        reloadTabCount() {
            TEMPLATE_SERVICES.getTabCountWellnessPlan(this.templateId).then(
                response => {
                    if (response?.code == 200) {
                        assignIn(this.tabCount, response.resources);
                    } else {
                        this._message(
                            "Error: Failed to fetch tabs' count",
                            "danger"
                        );
                    }
                }
            );
        },
        updateNonPlanDiscount(item = null, excludedCategories = []) {
            this.$refs.modalNonPlan.open(item, excludedCategories);
        },
        async reloadPage(tabName = "tabItems", reloadDetails = true) {
            if (tabName == "tabItems" && reloadDetails) {
                await this.reloadPageDetails();
            }
            this.reloadTabList(tabName);
            this.reloadTabCount();
        },
        reloadTabList(tabName) {
            this.activeTabIndex = this.findTabIndex(tabName);
            this.keyComponent++;
        },
        findTabIndex(tabname) {
            return findIndex(this.$refs.btabs.$children, function (tab) {
                return tab.$attrs.tabname == tabname;
            });
        },
        generateOptions(optionKey, options = {}) {
            const { append = "" } = options;

            return map(this.options[optionKey], (text, value) => ({
                value: Number(value),
                text: append ? `${text} ${append}` : text
            }));
        },
        generateLabelFee(fee) {
            let price = fee.costPlusPrice?.account_format;

            return `${fee.name} (${fee.type_category?.name}) (${price})}/${fee.measurement_unit?.value})`;
        }
    }
};
</script>

<style scoped>
::v-deep .custom-flex .bv-no-focus-ring {
    display: flex;
    align-items: center;
}
.top {
    margin-top: 10px;
    margin-bottom: 3px;
}
</style>
<style>
.title-alignment > div {
    margin-top: 0.5rem;
}
</style>
